import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../app/hooks';
import { setShowCommentsThread } from '../../store/commentsThread/commentsThread_actions';
import { AppNotification } from './NotificationCenter.types';
import {
    NotificationRoute,
    markNotificationDrawerOpened,
    resolveNotificationRoute,
    setPendingNotificationDrawer,
} from './notificationNavigation';

interface UseNotificationClickReturn {
    /** Null when the notification lacks the ids needed to route — callers should disable View Thread. */
    route: NotificationRoute | null;
    /** Performs the smart-routing click: navigate + parked drawer, or in-place drawer dispatch. */
    handleNotificationClick: () => void;
}

/**
 * Resolves the smart route for a notification and owns the click orchestration so the
 * item component stays presentational.
 */
export function useNotificationClick(notification: AppNotification): UseNotificationClickReturn {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const route = resolveNotificationRoute(notification, location.pathname, location.search);

    const handleNotificationClick = () => {
        if (!route) return;
        if (route.path) {
            // Park the drawer payload and open it after the route settles —
            // dispatching now would be lost to the destination/source page's
            // closeCommentsThread() unmount cleanup (see notificationNavigation.ts).
            if (route.drawerPayload) setPendingNotificationDrawer(route.drawerPayload);
            navigate(route.path);
        } else if (route.drawerPayload) {
            dispatch(setShowCommentsThread(route.drawerPayload));
            // Shield the in-place drawer from Topbar's tab-sync close (e.g. a late
            // config load re-deriving the selected tab on this same page).
            markNotificationDrawerOpened(location.pathname);
        }
    };

    return { route, handleNotificationClick };
}
